import { useQueryClient } from '@tanstack/react-query'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { ErrorResult } from '../../../../shared/models/types'
import { HouseConsignment, HouseConsignmentDetails } from '../models/types'
import useSaveHouseConsignment from './useSaveHouseConsignment'
import { QueryKeys } from '../../../key-registry'
import useDeleteHouseConsignmentDocuments from '../../house-consignment-document/components/useDeleteHouseConsignmentDocuments'
import useSaveHouseConsignmentTradersFormValues from './useSaveHouseConsingmentTraders'
import { fromHouseConsignmentDetails } from '../models/mapper'
import useSaveHouseConsignmentDocuments from '../../house-consignment-document/components/useSaveHouseConsignmentDocuments'
import { Document } from '../../document/models/types'
import computeConsignmentDocumentDeletes from '../../consignment-document/models/computeConsignmentDocumentDeletes'

interface SaveHouseConsignmentFormResult {
  data: HouseConsignmentFormValuesSaveResult
  hasPartialDocumentWrite: boolean
  errors: ErrorResult[]
}

export interface HouseConsignmentFormValuesSaveResult {
  houseConsignment: HouseConsignmentDetails
  houseConsignmentDocuments: Document[]
}

function useSaveHouseConsignmentForm(
  id: number | null,
  transitOperationId: number,
  consignmentId: number,
  existingDocuments: Document[],
) {
  const { saveHouseConsignment } = useSaveHouseConsignment()
  const { deleteHouseConsignmentDocuments } = useDeleteHouseConsignmentDocuments()
  const { saveHouseConsignmentDocuments } = useSaveHouseConsignmentDocuments(transitOperationId)
  const { saveTraders } = useSaveHouseConsignmentTradersFormValues(transitOperationId)

  const queryClient = useQueryClient()

  const writeHouseConsignment = async (
    formValues: HouseConsignmentFormValues,
    isDraft: boolean,
  ): Promise<SaveHouseConsignmentFormResult> => {
    const tradersResult = await saveTraders(isDraft, formValues)

    const { data: savedHouseConsignment, errors } = await saveHouseConsignment({
      id,
      isDraft,
      consignmentId,
      formValues,
      consigneeId: tradersResult.consignee?.id ?? null,
      consignorId: tradersResult.consignor?.id ?? null,
      sellerId: tradersResult.seller?.id ?? null,
      buyerId: tradersResult.buyer?.id ?? null,
    })
    if (!savedHouseConsignment?.id) throw new Error('House consignment save failed')

    const {
      data: savedDocuments,
      documentErrors: saveDocumentWriteErrors,
      errors: saveDocumentErrors,
    } = await saveHouseConsignmentDocuments(savedHouseConsignment.id, formValues)

    const documentsToDelete = computeConsignmentDocumentDeletes(existingDocuments, formValues)
    let deleteHouseConsignmentDocumentErrors: ErrorResult[] = []
    if (!saveDocumentWriteErrors.length) {
      const archiveResult = await deleteHouseConsignmentDocuments({ documentsToDelete })
      deleteHouseConsignmentDocumentErrors = archiveResult.errors
    }

    const allErrors = [
      ...errors,
      ...saveDocumentErrors,
      ...deleteHouseConsignmentDocumentErrors,
    ]

    return {
      data: {
        houseConsignment: savedHouseConsignment,
        houseConsignmentDocuments: savedDocuments ?? [],
      },
      hasPartialDocumentWrite: saveDocumentWriteErrors.length > 0,
      errors: allErrors,
    }
  }

  const updateCaches = async (args: SaveHouseConsignmentFormResult) => {
    const { houseConsignment: savedHouseConsignment, houseConsignmentDocuments: savedDocuments } = args.data
    if (!savedHouseConsignment?.id) throw new Error('Missing house consignment ID')

    queryClient.setQueryData(QueryKeys.houseConsignment.details(savedHouseConsignment.id), savedHouseConsignment)
    if (!args.hasPartialDocumentWrite) {
      queryClient.setQueryData(QueryKeys.houseConsignmentDocument.byHouseConsignmentId(savedHouseConsignment.id), savedDocuments)
    }
    queryClient.setQueryData<HouseConsignment[]>(QueryKeys.houseConsignment.all(consignmentId), (prev) => (prev
      ? prev.map((item) => {
        if (item.id === savedHouseConsignment.id) {
          return {
            ...item,
            ...fromHouseConsignmentDetails(savedHouseConsignment, item.numberOfGoods),
          }
        }
        return item
      }) : prev))
    await queryClient.invalidateQueries(QueryKeys.consignment.summary(consignmentId))
  }

  const saveHouseConsignmentFormValues = async ({
    values,
    isDraft,
    standalone = true,
  } : {
    values: HouseConsignmentFormValues
    isDraft: boolean
    standalone?: boolean
  }) => {
    const result = await writeHouseConsignment(values, isDraft)
    await updateCaches(result)

    await queryClient.invalidateQueries({
      queryKey: QueryKeys.transitOperation.validation({ transitOperationId }),
      refetchType: standalone ? 'active' : 'none',
    })

    return result
  }

  return {
    saveHouseConsignmentFormValues,
  }
}

export default useSaveHouseConsignmentForm
